import React from 'react';
import styled from 'styled-components';
import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';
import { graphql } from 'gatsby';

import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import LargeCard, { LargeCardData } from '@/components/LargeCard';
import { isDarkTheme, getFontColorByBackground } from '@/utils/StyleUtils';
import { mediaQuery } from '@/utils/helpers';
import { DefaultPaths } from '../Page';

type cardWrapperProps = {
  even: boolean;
};

const CardsWrapper = styled.ul<cardWrapperProps>`
  display: flex;
  flex-direction: column;
  padding: 0;
  list-style: none;
  gap: 2.4rem;
  margin: 0;

  ${({ even }) =>
    even
      ? `
      ${mediaQuery.sm(`
        display: grid;
        grid-template-columns: 1fr 1fr;
      `)};
      ${mediaQuery.md(`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
      `)};
    `
      : `
      ${mediaQuery.sm(`
        display: grid;
        grid-template-columns: 1fr
      `)};

      ${mediaQuery.md(`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      `)};
  `}
`;
type IndexProp = {
  index: number;
};
const BlockContent = styled.div<IndexProp>`
  text-align: center;
  ${({ index }) => {
    return `margin-bottom: ${index === 0 ? '7.2' : '5.6'}rem;`;
  }}
  *:last-child {
    margin-bottom: 0;
  }
`;

export type LargeCardBlockType = {
  id: string;
  index: number;
  __typename: string;
  title: string;
  subtitle: string;
  backgroundColor: string;
  cards: Array<LargeCardData>;
  noContainer: boolean;
  textColor?: string;
  defaultPaths?: DefaultPaths;
};
type Props = LargeCardBlockType;
const LargeCardBlock: React.FC<Props> = ({
  id,
  title,
  subtitle,
  backgroundColor,
  cards,
  noContainer,
  textColor,
  defaultPaths,
  index,
}) => {
  const dark = isDarkTheme(backgroundColor);
  const fontColor = textColor || getFontColorByBackground(backgroundColor);

  const getOddorEven = (amount: number): boolean => {
    return amount % 2 === 0 ? true : false;
  };
  const content = (
    <>
      {(title || subtitle) && (
        <BlockContent index={index}>
          {title && (
            <Title
              size={index === 0 ? 'xxxl' : 'xl'}
              level={index === 0 ? 'h1' : 'h2'}
              margin={index === 0 ? '2.4rem 0 1.6rem' : '0 0 1.6rem'}
              lightColor={fontColor === 'White'}
            >
              {title}
            </Title>
          )}
          {subtitle && (
            <Paragraph
              color={dark ? 'White' : 'GreyBlue'}
              margin='0 0 1.5rem'
              weight='semiBold'
            >
              {subtitle}
            </Paragraph>
          )}
        </BlockContent>
      )}
      <CardsWrapper even={getOddorEven(cards.length)}>
        {React.Children.toArray(
          cards.map((card) => {
            return LargeCard({
              ...card,
              parentDark: dark,
              defaultPaths: defaultPaths,
            });
          }),
        )}
      </CardsWrapper>
    </>
  );

  return (
    <BlockWrapper
      id={id}
      color={backgroundColor}
      padding={noContainer ? 'small' : 'normal'}
    >
      {noContainer ? content : <Container>{content}</Container>}
    </BlockWrapper>
  );
};

export default LargeCardBlock;
export const largeCardsBlockFragmentQuery = graphql`
  fragment LargeCardsBlockFragment on ContentfulLargeCardsBlock {
    id
    __typename
    title
    subtitle
    backgroundColor
    cards: largeCards {
      ...LargeCardFragment
    }
  }
`;
